import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buyerFundingLink,
  snapshotFromSession,
  writeBuyerFlowProgress,
  type BuyerFlowProgressStep
} from "../domain/buyerFlowProgress";
import {
  fetchBuyerSession,
  fetchEscrowPaymentRequest,
  persistBuyerFlowProgress,
  saveReview,
  fetchIntegrationsStatus,
  sendDeviceOtp,
  verifyDeviceOtp,
  validatePropertyAddress
} from "../api/client";
import {
  acknowledgeFlowReview,
  createOfficeReviewFromSession,
  reportFlowConflict,
  type BuyerFundingFlowReview
} from "../domain/buyerFundingFlowSmoke";
import { formatMoney } from "../domain/money";
import { validateDeviceOtp } from "../flows/buyerFunding/deviceVerification";
import {
  buyerFundingFlowLookupMatches,
  simulatePlaidConnect,
  skipPlaid
} from "../flows/buyerFunding/registry";
import {
  type BuyerFundingFlowPaymentInitiation,
  type BuyerFundingFlowAddressValidation,
  type BuyerFundingFlowDocuSignAck,
  type BuyerFundingFlowInstructionAck,
  type BuyerFundingFlowPlaidEvidence,
  type BuyerFundingFlowSession
} from "../flows/buyerFunding/types";
import { downloadSecuredBankDetails } from "../flows/buyerFunding/securedBankDetails";
import { BuyerClosingCongratulationsStep } from "./BuyerClosingCongratulationsStep";
import { BuyerDeviceValidationStep } from "./BuyerDeviceValidationStep";
import { BuyerDownloadInstructionsStep } from "./BuyerDownloadInstructionsStep";
import { BuyerIdentityVerifiedStep } from "./BuyerIdentityVerifiedStep";
import { BuyerInviteStep } from "./BuyerInviteStep";
import { PersonaIdentityPanel, type PersonaVerificationResult } from "./PersonaIdentityPanel";
import {
  recordFlowAddressValidation,
  recordFlowDocuSignAck,
  recordFlowInstructionAck,
  recordFlowPaymentInitiation,
  recordFlowPlaidEvidence
} from "../flows/buyerFunding/persist";
import { InstructionAckPanel } from "./InstructionAckPanel";
import { DocuSignAckPanel } from "./DocuSignAckPanel";
import { BuyerPaymentReviewStep } from "./BuyerPaymentReviewStep";
import { BuyerSourceAccountStep } from "./BuyerSourceAccountStep";
import { isAppClipContext } from "../domain/appClip";
import { AppClipBanner } from "./AppClipBanner";
import { VetoPageShell } from "./VetoPageShell";

type FlowStep =
  | "invite"
  | "lookup"
  | "device"
  | "persona"
  | "identity"
  | "instructions"
  | "download"
  | "esign"
  | "source"
  | "review"
  | "acknowledge"
  | "closing"
  | "complete";

const STEP_TITLES: Record<FlowStep, string> = {
  invite: "Review funding instructions",
  lookup: "Confirm your file",
  device: "Enter security code",
  persona: "Verify your identity",
  identity: "Identity recorded",
  instructions: "Funding instructions",
  download: "Download instructions",
  esign: "Sign acknowledgment",
  source: "Source account",
  review: "Payment status",
  acknowledge: "Acknowledge instructions",
  closing: "Steps recorded",
  complete: "Receipt"
};

export function BuyerFundingFlowApp({ fileCode }: { fileCode: string }) {
  const [published, setPublished] = useState<BuyerFundingFlowSession | undefined>();
  const [sessionLoading, setSessionLoading] = useState(true);
  const [officeReviewId, setOfficeReviewId] = useState("bf-001");
  const [session, setSession] = useState<BuyerFundingFlowSession | null>(null);
  const [step, setStep] = useState<FlowStep>("invite");
  const [lookupForm, setLookupForm] = useState({ fileCode: fileCode.toUpperCase(), buyerLastName: "", amountDue: "" });
  const [lookupError, setLookupError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [completionMessage, setCompletionMessage] = useState("Recorded for office review.");
  const [conflictSource, setConflictSource] = useState("");
  const [attackerRoutingLast4, setAttackerRoutingLast4] = useState("");
  const [attackerAccountLast4, setAttackerAccountLast4] = useState("");
  const [inviteSentAt, setInviteSentAt] = useState<string | undefined>();
  const [inviteBuyerLink, setInviteBuyerLink] = useState(() => buyerFundingLink(fileCode));
  const [deviceDevMode, setDeviceDevMode] = useState(false);
  const [twilioVerifyLive, setTwilioVerifyLive] = useState(false);
  const [deviceSending, setDeviceSending] = useState(false);
  const [deviceMaskedPhone, setDeviceMaskedPhone] = useState<string | undefined>();
  const [inAppClip, setInAppClip] = useState(false);
  const deviceOtpSentRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSessionLoading(true);
      const loaded = await fetchBuyerSession(fileCode);
      if (cancelled) return;
      if (loaded) {
        setPublished(loaded.session);
        setOfficeReviewId(loaded.reviewId);
      } else {
        setPublished(undefined);
      }
      setSessionLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fileCode]);

  useEffect(() => {
    setInAppClip(isAppClipContext());
  }, []);

  function officeReview(snapshot: BuyerFundingFlowSession): BuyerFundingFlowReview {
    return createOfficeReviewFromSession(snapshot, officeReviewId, `BFR-${snapshot.fileCode}`);
  }

  useEffect(() => {
    void fetchIntegrationsStatus().then((status) => {
      setTwilioVerifyLive(status.twilioVerify && status.buyerPhoneConfigured);
    });
  }, [fileCode]);

  useEffect(() => {
    void fetchEscrowPaymentRequest(fileCode).then((record) => {
      if (record?.buyerLink) {
        setInviteBuyerLink(record.buyerLink);
        setInviteSentAt(record.sentAt);
      } else {
        setInviteBuyerLink(buyerFundingLink(fileCode));
      }
    });
  }, [fileCode]);

  useEffect(() => {
    if (step !== "device" || !session) return;
    const sendKey = `${session.fileCode}:device`;
    if (deviceOtpSentRef.current === sendKey) return;
    deviceOtpSentRef.current = sendKey;
    setDeviceSending(true);
    void sendDeviceOtp(session.fileCode)
      .then((result) => {
        if (result.maskedPhone) setDeviceMaskedPhone(result.maskedPhone);
        const live = result.mode === "live";
        setDeviceDevMode(!live);
        setTwilioVerifyLive(live);
        setOtpError("");
      })
      .catch(() => {
        setDeviceDevMode(!twilioVerifyLive);
        setOtpError(
          twilioVerifyLive
            ? "Could not send the security code. Try Resend or contact your escrow office."
            : ""
        );
      })
      .finally(() => setDeviceSending(false));
  }, [step, session]);

  function resendDeviceOtp() {
    if (!session) return;
    deviceOtpSentRef.current = "";
    setDeviceSending(true);
    void sendDeviceOtp(session.fileCode)
      .then((result) => {
        deviceOtpSentRef.current = `${session.fileCode}:device`;
        if (result.maskedPhone) setDeviceMaskedPhone(result.maskedPhone);
        setDeviceDevMode(result.mode === "dev");
        setOtpError("");
      })
      .catch(() => setOtpError("Could not resend the code. Try again or contact your escrow office."))
      .finally(() => setDeviceSending(false));
  }

  const progressSession = session ?? published;

  useEffect(() => {
    if (!progressSession) return;
    const snapshot = snapshotFromSession(progressSession, step as BuyerFlowProgressStep);
    writeBuyerFlowProgress(snapshot);
    void persistBuyerFlowProgress(snapshot);
  }, [progressSession, step]);

  const handlePersonaVerified = useCallback((result: PersonaVerificationResult) => {
    const verifiedAt = new Date().toISOString();
    setSession((current) =>
      current
        ? {
            ...current,
            identityVerification: {
              verifiedAt,
              displayName: current.buyerName,
              mode: result.mode,
              personaInquiryId: result.inquiryId,
              personaStatus: result.status,
              limitation: result.limitation
            }
          }
        : current
    );
    setStep("identity");
  }, []);

  if (sessionLoading) {
    return (
      <VetoPageShell eyebrow="Buyer funding" title="Loading…">
        <section className="panel text-sm text-[#596158]">Loading file {fileCode}…</section>
      </VetoPageShell>
    );
  }

  if (!published) {
    return (
      <VetoPageShell eyebrow="Buyer funding" title="File not found">
        <section className="panel">
          <p className="mt-2 text-sm text-[#596158]">
            No published funding instructions exist for file code {fileCode}. Ask your escrow office for the
            correct link.
          </p>
        </section>
      </VetoPageShell>
    );
  }

  const active = session ?? published;
  const showInstructions = !["invite", "lookup", "device", "persona", "identity", "closing"].includes(step);
  const showInstructionDetails = ["instructions", "download"].includes(step);
  const showInstructionSummary = showInstructions && !showInstructionDetails;
  const showConflict = showInstructions && step !== "complete" && step !== "closing";

  function handleLookup(event: React.FormEvent) {
    event.preventDefault();
    if (!buyerFundingFlowLookupMatches(published!, lookupForm.fileCode, lookupForm.buyerLastName, lookupForm.amountDue)) {
      setLookupError("File lookup details did not match. Check file code, buyer last name, and amount due.");
      return;
    }
    setLookupError("");
    const nextSession = { ...published! };
    setSession(nextSession);
    void validatePropertyAddress(nextSession.propertyAddress, nextSession.fileCode)
      .then((validation: BuyerFundingFlowAddressValidation) => {
        setSession((current) => (current ? { ...current, addressValidation: validation } : current));
      })
      .catch(() => {
        /* Google key optional; flow continues. */
      });
    setStep("device");
  }

  async function handleDeviceSubmit(event: React.FormEvent) {
    event.preventDefault();
    const flowSession = session ?? published!;
    let verified = false;
    let mode: "live" | "simulated" = twilioVerifyLive ? "live" : "simulated";
    let limitation = twilioVerifyLive
      ? "Phone number verified with Twilio Verify at time of check."
      : "Simulated SMS OTP for development. Production uses Twilio Verify when configured on the server.";

    try {
      const result = await verifyDeviceOtp(flowSession.fileCode, otp);
      verified = result.verified;
      if (result.verified && result.mode === "live") {
        mode = "live";
        setTwilioVerifyLive(true);
        limitation = "Phone number verified with Twilio Verify at time of check.";
      } else if (!result.verified && validateDeviceOtp(otp)) {
        verified = true;
        limitation = result.error
          ? `Accepted demo OTP. ${result.error}`
          : "Accepted demo OTP while live verification was unavailable.";
      }
    } catch {
      if (validateDeviceOtp(otp)) {
        verified = true;
        limitation = "Accepted demo OTP while the verification service was unavailable.";
      }
    }

    if (!verified) {
      setOtpError("That security code did not match. Check the code and try again.");
      return;
    }

    setOtpError("");
    const verifiedAt = new Date().toISOString();
    setSession((current) =>
      current
        ? {
            ...current,
            deviceVerification: {
              maskedPhone: deviceMaskedPhone || current.invitePhoneMasked,
              verifiedAt,
              mode,
              limitation
            }
          }
        : current
    );
    setStep("persona");
  }

  function handleIdentityContinue() {
    setStep("instructions");
  }

  function handleDownload() {
    downloadSecuredBankDetails(active);
    const at = new Date().toISOString();
    setDownloaded(true);
    setSession((current) => (current ? { ...current, instructionsDownloadedAt: at } : current));
  }

  async function persistReview(review: BuyerFundingFlowReview) {
    await saveReview(review);
  }

  function mergeFlowEvidence(
    review: BuyerFundingFlowReview,
    flowSession: BuyerFundingFlowSession = active
  ): BuyerFundingFlowReview {
    return {
      ...review,
      buyerFundingFlow: {
        ...review.buyerFundingFlow,
        invitePhoneMasked: flowSession.invitePhoneMasked,
        deviceVerifiedAt: flowSession.deviceVerification?.verifiedAt,
        identityVerifiedAt: flowSession.identityVerification?.verifiedAt,
        personaInquiryId: flowSession.identityVerification?.personaInquiryId,
        personaStatus: flowSession.identityVerification?.personaStatus,
        instructionsDownloadedAt: flowSession.instructionsDownloadedAt,
        paymentRequestId: flowSession.paymentRequest.requestId,
        paymentInitiatedAt: flowSession.paymentInitiation?.initiatedAt,
        paymentInitiationReference: flowSession.paymentInitiation?.reference,
        buyerViewedAt: flowSession.instructionsDownloadedAt || review.buyerFundingFlow.buyerViewedAt,
        buyerAcknowledgedAt: flowSession.acknowledgedAt || review.buyerFundingFlow.buyerAcknowledgedAt,
        addressValidationAt: flowSession.addressValidation?.validatedAt,
        addressValidationPlaceId: flowSession.addressValidation?.placeId,
        addressValidationVerdict: flowSession.addressValidation?.verdict,
        instructionAckMethod: flowSession.instructionAck?.method,
        instructionAckCredentialId: flowSession.instructionAck?.passkeyCredentialId,
        docuSignEnvelopeId: flowSession.docuSignAck?.envelopeId,
        docuSignSignedAt: flowSession.docuSignAck?.signedAt,
        docuSignInstructionHash: flowSession.docuSignAck?.instructionHash,
        docuSignDocumentHash: flowSession.docuSignAck?.documentHash,
        docuSignSignerEmail: flowSession.docuSignAck?.signerEmail
      }
    };
  }

  function applyFlowEvidenceToReview(
    review: BuyerFundingFlowReview,
    flowSession: BuyerFundingFlowSession = active
  ): BuyerFundingFlowReview {
    let next = mergeFlowEvidence(review, flowSession);
    if (flowSession.plaid) {
      next = recordFlowPlaidEvidence(next, flowSession.plaid);
    }
    if (flowSession.paymentInitiation) {
      next = recordFlowPaymentInitiation(next, flowSession.paymentInitiation);
    }
    if (flowSession.addressValidation) {
      next = recordFlowAddressValidation(next, flowSession.addressValidation);
    }
    if (flowSession.docuSignAck) {
      next = recordFlowDocuSignAck(next, flowSession.docuSignAck);
    } else if (flowSession.instructionAck) {
      next = recordFlowInstructionAck(next, flowSession.instructionAck);
    }
    return next;
  }

  async function handleDocuSignSigned(ack: BuyerFundingFlowDocuSignAck) {
    const snapshot = {
      ...active,
      docuSignAck: ack,
      acknowledgedAt: ack.signedAt
    };
    setSession(snapshot);
    setStep("source");
    const review = applyFlowEvidenceToReview(officeReview(snapshot), snapshot);
    try {
      await persistReview({ ...review, updatedAt: ack.signedAt });
    } catch {
      /* Buyer flow continues even if office review API is unavailable locally. */
    }
  }

  function handleSourceRecorded(evidence: BuyerFundingFlowPlaidEvidence) {
    setSession((current) => (current ? { ...current, plaid: evidence } : current));
    setStep("review");
  }

  function handleSourceSkip() {
    setSession((current) => (current ? skipPlaid(current) : current));
    setStep("review");
  }

  function handlePlaidSimulate() {
    setSession((current) => (current ? simulatePlaidConnect(current) : current));
    setStep("review");
  }

  function handlePaymentInitiated(initiation: BuyerFundingFlowPaymentInitiation) {
    setSession((current) => (current ? { ...current, paymentInitiation: initiation } : current));
    if (active.docuSignAck) {
      setStep("closing");
    } else {
      setStep("acknowledge");
    }
  }

  async function handleInstructionAck(ack: BuyerFundingFlowInstructionAck) {
    const acknowledgedAt = ack.acknowledgedAt;
    const snapshot = { ...active, acknowledgedAt, instructionAck: ack };
    setSession(snapshot);
    const review = applyFlowEvidenceToReview(acknowledgeFlowReview(officeReview(snapshot), snapshot), snapshot);
    await persistReview({ ...review, updatedAt: acknowledgedAt });
    setStep("closing");
  }

  async function handleConflict(event: React.FormEvent) {
    event.preventDefault();
    let review = applyFlowEvidenceToReview(acknowledgeFlowReview(officeReview(active), active));
    review = reportFlowConflict(review, active, conflictSource, attackerRoutingLast4, attackerAccountLast4);
    await persistReview(review);
    setStep("complete");
    setCompletionMessage("Conflict report recorded for office review.");
  }

  const instructions = active.instructions;

  return (
    <VetoPageShell
      eyebrow={`${active.officeName} / File ${active.fileCode}`}
      title={STEP_TITLES[step]}
      fileMeta={`${active.propertyAddress}. Due ${formatMoney(active.amountDue)}`}
      compact
    >
      {inAppClip ? <AppClipBanner /> : null}

      {step === "invite" ? (
        <BuyerInviteStep
          officeName={active.officeName}
          fileCode={active.fileCode}
          buyerName={active.buyerName}
          buyerEmail={active.buyerEmail}
          amountDueLabel={formatMoney(active.amountDue)}
          paymentRequest={active.paymentRequest}
          buyerLink={inviteBuyerLink}
          sentAt={inviteSentAt}
          onGetStarted={() => {
            setStep("lookup");
          }}
        />
      ) : null}

      {step === "lookup" ? (
        <section className="panel">
          <form className="grid gap-3" onSubmit={handleLookup}>
            <label className="grid gap-1">
              <span className="field-label">File code</span>
              <input
                data-testid="buyer-file-code"
                value={lookupForm.fileCode}
                onChange={(e) => setLookupForm((f) => ({ ...f, fileCode: e.target.value }))}
              />
            </label>
            <label className="grid gap-1">
              <span className="field-label">Buyer last name</span>
              <input
                data-testid="buyer-last-name"
                value={lookupForm.buyerLastName}
                onChange={(e) => setLookupForm((f) => ({ ...f, buyerLastName: e.target.value }))}
              />
            </label>
            <label className="grid gap-1">
              <span className="field-label">Amount due</span>
              <input
                data-testid="buyer-amount-due"
                placeholder="e.g. 184217.42"
                value={lookupForm.amountDue}
                onChange={(e) => setLookupForm((f) => ({ ...f, amountDue: e.target.value }))}
              />
            </label>
            <button className="button-primary" data-testid="buyer-continue" type="submit">
              Confirm file details
            </button>
          </form>
          {lookupError ? <p className="mt-3 text-sm text-[#8a2d10]">{lookupError}</p> : null}
        </section>
      ) : null}

      {step === "device" ? (
        <BuyerDeviceValidationStep
          maskedPhone={deviceMaskedPhone || active.invitePhoneMasked}
          otp={otp}
          otpError={otpError}
          devMode={deviceDevMode && !twilioVerifyLive}
          sending={deviceSending}
          onOtpChange={setOtp}
          onSubmit={handleDeviceSubmit}
          onResend={resendDeviceOtp}
        />
      ) : null}

      {step === "persona" ? (
        <PersonaIdentityPanel session={active} onVerified={handlePersonaVerified} />
      ) : null}

      {step === "identity" ? (
        <BuyerIdentityVerifiedStep
          displayName={active.identityVerification?.displayName || active.buyerName}
          maskedPhone={active.deviceVerification?.maskedPhone || active.invitePhoneMasked}
          mode={active.identityVerification?.mode}
          onContinue={handleIdentityContinue}
        />
      ) : null}

      {showInstructionDetails ? (
        <section className="panel border-[#9ebfaf] bg-[#fbfdfb]" data-testid="buyer-instructions">
          <div className="flex items-start justify-between gap-4 border-b border-[#dce7e1] pb-4">
            <div>
              <p className="text-sm text-[#5c665f]">
                Version {instructions.versionLabel}. Due {new Date(instructions.deadlineAt).toLocaleString()}
              </p>
            </div>
            <p className="shrink-0 text-xl font-bold tracking-[-0.025em]">{formatMoney(active.amountDue)}</p>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div className="col-span-2">
              <dt className="font-bold">Beneficiary</dt>
              <dd>{instructions.beneficiaryName}</dd>
            </div>
            <div>
              <dt className="font-bold">Bank</dt>
              <dd>{instructions.bankName}</dd>
            </div>
            <div>
              <dt className="font-bold">Routing (last 4)</dt>
              <dd>{instructions.routingLast4}</dd>
            </div>
            <div>
              <dt className="font-bold">Account (last 4)</dt>
              <dd>{instructions.accountLast4}</dd>
            </div>
            <div className="col-span-2">
              <dt className="font-bold">Required memo / file reference</dt>
              <dd>{instructions.memoRequired}</dd>
            </div>
            <div className="col-span-2">
              <dt className="font-bold">Property</dt>
              <dd>{active.propertyAddress}</dd>
            </div>
          </dl>
          {instructions.officeContactNote ? (
            <p className="notice mt-4 text-sm">{instructions.officeContactNote}</p>
          ) : null}
        </section>
      ) : null}

      {showInstructionSummary ? (
        <details className="panel disclosure-panel" data-testid="buyer-instructions">
          <summary>
            Secured instructions
            <span className="ml-2 font-normal text-[#5c665f]">{formatMoney(active.amountDue)}</span>
          </summary>
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[#e3e1da] pt-4 text-sm">
            <div className="col-span-2">
              <dt className="text-xs font-semibold text-[#5c665f]">Beneficiary</dt>
              <dd className="mt-0.5 font-semibold">{instructions.beneficiaryName}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-[#5c665f]">Bank</dt>
              <dd className="mt-0.5">{instructions.bankName}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-[#5c665f]">Account</dt>
              <dd className="mt-0.5">Ending {instructions.accountLast4}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-xs font-semibold text-[#5c665f]">File reference</dt>
              <dd className="mt-0.5">{instructions.memoRequired}</dd>
            </div>
          </dl>
        </details>
      ) : null}

      {step === "instructions" ? (
        <div className="flex flex-wrap gap-2">
          <button className="button-primary" type="button" data-testid="buyer-open-download" onClick={() => setStep("download")}>
            Download secured details
          </button>
        </div>
      ) : null}

      {step === "download" ? (
        <BuyerDownloadInstructionsStep
          session={active}
          downloaded={downloaded}
          onDownload={handleDownload}
          onContinue={() => {
            setStep("esign");
          }}
        />
      ) : null}

      {step === "esign" ? <DocuSignAckPanel session={active} onSigned={(ack) => void handleDocuSignSigned(ack)} /> : null}

      {step === "source" ? (
        <BuyerSourceAccountStep
          fileCode={active.fileCode}
          buyerName={active.buyerName}
          onPlaidConnected={handleSourceRecorded}
          onManualRecorded={handleSourceRecorded}
          onSkip={handleSourceSkip}
          onPlaidSimulate={handlePlaidSimulate}
        />
      ) : null}

      {step === "review" ? (
        <BuyerPaymentReviewStep session={active} onInitiated={handlePaymentInitiated} />
      ) : null}

      {step === "acknowledge" ? (
        <InstructionAckPanel
          fileCode={active.fileCode}
          instructions={instructions}
          ackChecked={ackChecked}
          onAckCheckedChange={setAckChecked}
          onSubmit={(ack) => void handleInstructionAck(ack)}
        />
      ) : null}

      {step === "closing" ? (
        <BuyerClosingCongratulationsStep
          onDone={() => {
            setStep("complete");
            setCompletionMessage("Buyer acknowledgment recorded for office review.");
          }}
        />
      ) : null}

      {step === "complete" ? (
        <section className="panel border-[#a9c8b9] bg-[#e6f0eb]">
          <p className="text-sm text-[#36594d]">{completionMessage}</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-[#c9ddd3] pt-4 text-sm">
            <div>
              <dt className="text-xs font-semibold text-[#507064]">File</dt>
              <dd className="mt-0.5 font-bold">{active.fileCode}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-[#507064]">Buyer</dt>
              <dd className="mt-0.5 font-bold">{active.buyerName}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-[#507064]">Instructions</dt>
              <dd className="mt-0.5 font-bold">Version {instructions.versionLabel}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-[#507064]">Office status</dt>
              <dd className="mt-0.5 font-bold">Recorded for review</dd>
            </div>
          </dl>
        </section>
      ) : null}

      {showConflict ? (
        <details className="panel disclosure-panel">
          <summary>Received different wire details?</summary>
          <form className="mt-4 grid gap-3" onSubmit={handleConflict}>
            <label className="grid gap-1">
              <span className="field-label">Where did you see different instructions?</span>
              <input
                data-testid="conflict-source"
                value={conflictSource}
                onChange={(e) => setConflictSource(e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="field-label">Routing last four shown</span>
              <input
                data-testid="conflict-routing-last4"
                value={attackerRoutingLast4}
                onChange={(e) => setAttackerRoutingLast4(e.target.value)}
              />
            </label>
            <label className="grid gap-1">
              <span className="field-label">Account last four shown</span>
              <input
                data-testid="conflict-account-last4"
                value={attackerAccountLast4}
                onChange={(e) => setAttackerAccountLast4(e.target.value)}
              />
            </label>
            <button
              className="button-secondary"
              data-testid="report-conflict"
              disabled={!conflictSource || !attackerRoutingLast4 || !attackerAccountLast4}
              type="submit"
            >
              Send report to the office
            </button>
          </form>
        </details>
      ) : null}
    </VetoPageShell>
  );
}
